import React from 'react';
import { FP610 } from '../../../services/p251Api';
import LookupField from '../../../components/LookupField';
import { dateToInput, inputToDate, fmt } from '../helpers';

interface Tab1Props {
  formData: Partial<FP610>;
  isEditing: boolean;
  isPCS: boolean;
  getField: (key: keyof FP610) => FP610[keyof FP610];
  setField: (key: keyof FP610, value: FP610[keyof FP610]) => void;
  onShipUnitChange: (val: number) => void;
  onKnitTypeChange: () => void;
}

const Tab1BasicInfo: React.FC<Tab1Props> = React.memo(({
  formData, isEditing, isPCS,
  getField, setField,
  onShipUnitChange, onKnitTypeChange,
}) => {
  return (
    <div className="form-sections">
      {/* Section 1: S/C & Product */}
      <div className="form-section odd">
        <div className="section-tag">基本資訊</div>
        <div className="form-grid">
          <label>S/C NO</label>
          <div>
            <input
              type="text"
              maxLength={6}
              value={String(getField('fp610_10') || '')}
              onChange={e => setField('fp610_10', e.target.value)}
              disabled={!isEditing}
            />
          </div>

          <label>產品代號</label>
          <div>
            <LookupField
              lookupType="product"
              maxLength={12}
              value={String(getField('fp610_29') || '')}
              onChange={v => setField('fp610_29', v)}
              onCodeConfirm={v => setField('fp610_29', v)}
              disabled={!isEditing}
            />
          </div>

          <label>日    期</label>
          <div>
            <input
              type="date"
              value={dateToInput(getField('fp610_11') as string | null)}
              onChange={e => setField('fp610_11', inputToDate(e.target.value) as string)}
              disabled={!isEditing}
            />
          </div>
        </div>
      </div>

      {/* Section 2: SHIPPING MARK & DESCRIPTION */}
      <div className="form-section even">
        <div className="section-tag">描述</div>
        <div className="form-grid two-col">
          <label>SHIPPING MARK</label>
          <div>
            <textarea
              rows={3}
              value={String(getField('fp610_13') || '')}
              onChange={e => setField('fp610_13', e.target.value)}
              disabled={!isEditing}
            />
          </div>

          <label>DESCRIPTION OF GOODS</label>
          <div>
            <textarea
              rows={3}
              value={String(getField('fp610_14') || '')}
              onChange={e => setField('fp610_14', e.target.value)}
              disabled={!isEditing}
            />
          </div>

          <label>材    積</label>
          <div>
            <input
              type="text"
              maxLength={50}
              value={String(getField('fp610_15') || '')}
              onChange={e => setField('fp610_15', e.target.value)}
              disabled={!isEditing}
            />
          </div>
        </div>
      </div>

      {/* Section 3: Customer & Options */}
      <div className="form-section odd">
        <div className="section-tag">客戶 / 選項</div>
        <div className="form-grid">
          <label>客戶代號</label>
          <div>
            <LookupField
              lookupType="customer"
              maxLength={12}
              value={String(getField('fp610_25') || '')}
              onChange={v => setField('fp610_25', v)}
              onCodeConfirm={v => setField('fp610_25', v)}
              disabled={!isEditing}
            />
          </div>

          <label>包裝單位</label>
          <div className="radio-group">
            {[
              { val: 1, label: 'R/NO.' },
              { val: 2, label: 'B/NO.' },
              { val: 3, label: 'C/NO.' },
            ].map(opt => (
              <label key={opt.val}>
                <input
                  type="radio"
                  name="fp610_21"
                  value={opt.val}
                  checked={(getField('fp610_21') as number) === opt.val}
                  onChange={() => setField('fp610_21', opt.val)}
                  disabled={!isEditing}
                />
                {opt.label}
              </label>
            ))}
          </div>

          <label>內外銷別</label>
          <div className="radio-group">
            {[
              { val: 1, label: '外銷' },
              { val: 2, label: '內銷' },
            ].map(opt => (
              <label key={opt.val}>
                <input
                  type="radio"
                  name="fp610_26"
                  value={opt.val}
                  checked={(getField('fp610_26') as number) === opt.val}
                  onChange={() => setField('fp610_26', opt.val)}
                  disabled={!isEditing}
                />
                {opt.label}
              </label>
            ))}
          </div>

          <label>出貨單位</label>
          <div className="radio-group">
            {[
              { val: 1, label: 'YDS' },
              { val: 2, label: 'MTS' },
              { val: 3, label: 'PCS' },
            ].map(opt => (
              <label key={opt.val}>
                <input
                  type="radio"
                  name="fp610_27"
                  value={opt.val}
                  checked={(getField('fp610_27') as number) === opt.val}
                  onChange={() => onShipUnitChange(opt.val)}
                  disabled={!isEditing}
                />
                {opt.label}
              </label>
            ))}
          </div>

          {!isPCS && (
            <>
              <label>針織/平織</label>
              <div className="radio-group">
                {[
                  { val: 1, label: '平織' },
                  { val: 2, label: '針織' },
                ].map(opt => (
                  <label key={opt.val}>
                    <input
                      type="radio"
                      name="fp610_28"
                      value={opt.val}
                      checked={(getField('fp610_28') as number) === opt.val}
                      onChange={() => {
                        setField('fp610_28', opt.val);
                        onKnitTypeChange();
                      }}
                      disabled={!isEditing}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Section 4: Totals (readonly) */}
      <div className="form-section even">
        <div className="section-tag">彙總</div>
        <div className="form-grid">
          <label>總 碼 數</label>
          <div>
            <input type="text" readOnly value={fmt(formData.fp610_17, 1)} />
          </div>

          <label>總 重 量</label>
          <div>
            <input type="text" readOnly value={fmt(formData.fp610_19, 1)} />
          </div>

          {!isPCS && (
            <>
              <label>總 米 數</label>
              <div>
                <input type="text" readOnly value={fmt(formData.fp610_18, 1)} />
              </div>
            </>
          )}

          <label>總 淨 量</label>
          <div>
            <input type="text" readOnly value={fmt(formData.fp610_20, 1)} />
          </div>

        </div>
      </div>
    </div>
  );
});

Tab1BasicInfo.displayName = 'Tab1BasicInfo';
export default Tab1BasicInfo;
